import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useIntl } from "react-intl";

const SearchableDialog = ({
  open,
  onClose,
  title,
  options = [],
  selectedValue,
  onOptionSelect,
  searchPlaceholder,
  noResultsText,
  primaryColor,
}) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState("");

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  // Default filter function (case-insensitive substring matching)
  const filterFunction = (option, query) => {
    if (!query.trim()) return true;
    const searchLower = query.toLowerCase();
    return (
      option.text?.toLowerCase().includes(searchLower) ||
      option.optionText?.toLowerCase().includes(searchLower)
    );
  };

  // Filter options based on search query
  const filteredOptions = options.filter((option) =>
    filterFunction(option, searchQuery)
  );

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      aria-labelledby="mobile-view-modal"
      id="mobile-view-modal"
    >
      <DialogTitle id="mobile-view-modal-title">
        <div className="modal-header">
          <h5 className="mr-12 text-lg font-bold truncate">
            {title || intl.formatMessage({ id: "select" })}
          </h5>
          {/* close button */}
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <span className="sr-only">
              {intl.formatMessage({ id: "close" })}
            </span>
          </button>
        </div>
      </DialogTitle>

      <DialogContent id="mobile-view-modal-body">
        {/* Search Input */}
        <div className="sticky top-0 z-10 p-1 bg-white border-b border-gray-200">
          <TextField
            fullWidth
            variant="outlined"
            placeholder={
              searchPlaceholder ||
              intl.formatMessage(
                { id: "search" },
                { defaultMessage: "Search..." }
              )
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#f5f5f5",
                padding: "4px 8px",
                "& .MuiInputBase-input": {
                  padding: "8px 8px",
                },
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: primaryColor || "#9c27b0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor || "#9c27b0",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchQuery("")}
                    edge="end"
                    aria-label="Clear search"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        {/* Options List */}
        <div className="divide-y divide-gray-200">
          {filteredOptions && filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              return (
                <div
                  key={index}
                  className="flex items-center justify-between px-3 py-3 bg-white"
                >
                  <label
                    htmlFor={`skill-${index}`}
                    className="flex-1 block text-base font-medium cursor-pointer"
                  >
                    {option.text}
                    {option?.optionText ? (
                      <p
                        style={{
                          color: primaryColor,
                          fontSize: "12px",
                        }}
                      >
                        {option?.optionText}
                      </p>
                    ) : (
                      ""
                    )}
                  </label>
                  <input
                    type="radio"
                    id={`skill-${index}`}
                    name="radioType"
                    value={option._id}
                    className="w-5 h-5 border-gray-300 rounded-full cursor-pointer"
                    onChange={() => onOptionSelect(option)}
                    checked={selectedValue === option._id}
                  />
                </div>
              );
            })
          ) : (
            <div className="flex items-center justify-center px-3 py-8 text-center">
              <div>
                <p className="text-base font-medium text-gray-500">
                  {noResultsText ||
                    intl.formatMessage({ id: "no-results-found" })}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchableDialog;
